import React from "react";
import ThemeProvider from "@onnit-js/ui/components/themes/ThemeProvider";
import GlobalStyle from "@onnit-js/ui/components/themes/GlobalStyle";
import theme from "@onnit-js/ui/components/themes/light";
import Box from "@onnit-js/ui/components/box/Box";
import { List, ListItem, Text } from "@onnit-js/ui/components/text";
import useOnnitContext from "@onnit-js/ui/hooks/useOnnitContext";
import { Img, Picture } from "@onnit-js/ui/components/image";
import ProductImageGrid from "@onnit-js/ui/components/module/ProductImageGrid";
import TwelveColumn from "@onnit-js/ui/components/module/grid/12/Grid";
import Sup from "@onnit-js/ui/components/text/Sup";
import MediaContentShowcase from "@onnit-js/ui/components/module/MediaContentShowcase";
import usePageData from "../../hooks/usePageData";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import SupplementFacts from "../../components/common/SupplementFacts";
import ingredients from "./ingredients";
import config from "../../config/config";

const transformProductName = (name: string): string => name.replace(/Total Nitric Oxide® - /giu, "");

export default function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();

    if (!pageData || !onnitContext) {
        return null;
    }

    const IMG_URL = `${onnitContext.url.cdn.static_images}/product-page/total-nitric-oxide`;
    const { IMG_PARAMS } = config;
    const mainColor = "#E3627F";

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />

                <DefaultProductPageTop productNameTransform={transformProductName}>
                    <ProductImageGrid>
                        <Picture
                            lazyLoad={false}
                            width={1943}
                            height={873}
                            alt="Container of total nitric acid with fruit in the background"
                            src={[
                                `${IMG_URL}/2024-TNO-1-920x920.png${IMG_PARAMS}`,
                                `${IMG_URL}/2024-TNO-1-1940x873.png${IMG_PARAMS}`,
                            ]}
                        />
                        <Img
                            alt="beets"
                            src={`${IMG_URL}/2024-TNO-Wpage-Gallery-2-920x920.png${IMG_PARAMS}`}
                            lazyLoad={false}
                        />
                        <Img
                            alt="mango"
                            src={`${IMG_URL}/2024-TNO-Wpage-Gallery-3-920x920.png${IMG_PARAMS}`}
                            lazyLoad={false}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <Box bg="#161616" mt={[0, 0, 6, 8]} py={[6, 6, 6, 8]}>
                    <TwelveColumn display={["none", "none", "grid"]} mb={4}>
                        <Box gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 7"]} px={4}>
                            <Text as="h2" color="grays.3" typeStyle="text02" fontWeight="regular">
                                Total Nitric Oxide
                                <Sup variant="reg" />
                            </Text>
                        </Box>
                    </TwelveColumn>
                    <TwelveColumn display={["block", "block", "grid"]} mb={[0, 0, 6]}>
                        <Box
                            gridColumn={["7 / span 6", "7 / span 6", "7 / span 6", "7 / span 5"]}
                            px={4}
                            mb={[6, 6, 0]}
                        >
                            <Img
                                src={`${IMG_URL}/2024-TNO-Hero-1920x1080.png${IMG_PARAMS}`}
                                alt="woman using kettlebell"
                            />
                        </Box>
                        <Box
                            gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 5"]}
                            display={["block", "block", "flex"]}
                            alignItems="center"
                            px={4}
                        >
                            <Text
                                display={["block", "block", "none"]}
                                as="h2"
                                color="grays.3"
                                typeStyle="text02"
                                fontWeight="regular"
                                my={3}
                            >
                                Total Nitric Oxide
                                <Sup variant="reg" />
                            </Text>

                            <Text as="h3" typeStyle="title01" color={mainColor} mb={2}>
                                Prime Your Pump <Sup />
                            </Text>
                        </Box>
                    </TwelveColumn>

                    <TwelveColumn display={["block", "block", "grid"]}>
                        <Box
                            gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 7"]}
                            px={4}
                            mb={[6, 6, 6, 8]}
                        >
                            <Text as="p" color="white" typeStyle="text02" mb={4} fontWeight="regular">
                                Nitric oxide (NO) is naturally produced in the body from the amino acids L-arginine and
                                L-citrulline, and it assists with vasodilation—a temporary widening of the blood
                                vessels. Vasodilation helps with blood flow to working muscles, contributing to the
                                delivery of nutrients and oxygen as well as the removal of metabolic waste products that
                                cause muscle fatigue.
                                <Sup />
                            </Text>
                            <Text as="p" color="white" typeStyle="text02" mb={4} fontWeight="regular">
                                By supporting your body's NO production, Total Nitric Oxide<Sup variant="reg" /> promotes exercise
                                performance as well as muscle recovery. Plus, the extra blood flow to your muscles while
                                lifting weights improves your pump—the "swole" look gym rats love!
                            </Text>
                        </Box>
                        <Box gridColumn={["2 / span 10", "2 / span 10", "2 / span 10", "3 / span 10"]} px={4}>
                            <Text as="h3" typeStyle="title01" color={mainColor} mb={4}>
                                Total Nitric Oxide
                                <Sup variant="reg" /> is for:
                            </Text>
                            <List>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular" mb={3}>
                                    People who lift weights{" "}
                                </ListItem>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular" mb={3}>
                                    Runners and endurance athletes
                                </ListItem>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular">
                                    Anyone seeking the best results from their workouts
                                </ListItem>
                            </List>
                        </Box>
                    </TwelveColumn>
                </Box>

                <Box bg="#161616" py={[6, 6, 6, 8]}>
                    <TwelveColumn display={["block", "block", "block", "grid"]} px={5} mb={6}>
                        <Text
                            as="h3"
                            typeStyle="title02"
                            color="white"
                            gridColumn={["1 / -1", "1 / -1", "2 / span 12"]}
                        >
                            Key Ingredients
                        </Text>
                    </TwelveColumn>
                    <MediaContentShowcase
                        showImages={false}
                        items={ingredients.map((item) => {
                            const copy = { ...item };
                            copy.image = `${IMG_URL}/${copy.image}`;
                            return copy;
                        })}
                        activeColor={mainColor}
                        textColor="white"
                        bgMedia={(
                            <Img
                                alt="collection of nuts and seeds"
                                src={`${IMG_URL}/2024-TNO-Wpage-Ingredient-Background.png${IMG_PARAMS}`}
                                width={1120}
                                height={315}
                            />
                          )}
                    />
                </Box>

                <UsageAndDisclaimers supplementFacts={<SupplementFacts />}>
                    <Box>
                        <Text as="p" fontSize={0} color="grays.4" fontWeight="heavy" display="inline" mr={1}>
                            WARNING:
                        </Text>
                        <Text as="p" fontSize={0} display="inline" typeStyle="default" color="grays.4" mb={4}>
                            Consult a medical doctor before this or any other nutritional supplement if you are
                            pregnant, nursing, have or suspect a medical condition or are taking any medications. Do not
                            use if you are currently taking nitrates for chest pain or if you are taking medication to
                            treat erectile dysfunction such as PDE-5 inhibitors. Store in a cool, dry place. Do not use
                            it if the freshness seal is missing or broken.{" "}
                            <span className="bold">KEEP OUT OF REACH OF CHILDREN.</span>
                        </Text>
                    </Box>
                    <Text as="p" mt={3} fontSize={1} color="grays.5">
                        Careflow<Sup variant="reg" /> is a trademark of Vital Solutions GmbH, Germany. OxyPhyte<Sup variant="reg" /> is a registered trademark
                        of RFI, LLC. NO3-T<Sup variant="reg" /> is a registered trademark of ThermoLife International, LLC.
                        www.NO3-T.com/patents
                    </Text>
                </UsageAndDisclaimers>
            </>
        </ThemeProvider>
    );
}
